import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface FactRatingProps {
  rating: 'high' | 'medium' | 'low';
  postId: string;
}

const FactRating: React.FC<FactRatingProps> = ({ rating, postId }) => {
  const { currentUser } = useAuth();
  const [isVoteModalOpen, setIsVoteModalOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState<'high' | 'medium' | 'low'>(rating);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const getRatingBars = (type: 'current' | 'voting') => {
    const bars = [];
    const isVoting = type === 'voting';
    const ratingToUse = isVoting ? selectedRating : rating;
    
    for (let i = 0; i < 3; i++) {
      let barColor = 'bg-[#505050]';
      
      if (ratingToUse === 'high' && i < 3) {
        barColor = 'bg-green-500';
      } else if (ratingToUse === 'medium' && i < 2) {
        barColor = 'bg-yellow-400';
      } else if (ratingToUse === 'low' && i < 1) {
        barColor = 'bg-red-500';
      }
      
      bars.push(
        <button
          key={i}
          onClick={() => {
            if (isVoting) {
              setSelectedRating(
                i === 0 ? 'low' : i === 1 ? 'medium' : 'high'
              );
            }
          }}
          disabled={!isVoting}
          className={`h-1.5 rounded-full transition-all duration-200 ${
            isVoting ? 'w-16 cursor-pointer hover:opacity-80' : 'w-4'
          } ${barColor}`}
        />
      );
    }
    
    return bars;
  };

  const handleSubmitVote = async () => {
    if (!currentUser || !reason.trim()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('fact_rating_votes')
        .insert({
          post_id: postId,
          user_id: currentUser.id,
          rating: selectedRating,
          reason: reason.trim()
        });

      if (error) throw error;

      setIsVoteModalOpen(false);
      setReason('');
    } catch (err) {
      console.error('Error submitting vote:', err);
      setError('Failed to submit vote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = isVoteModalOpen && createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={() => setIsVoteModalOpen(false)}
      />
      
      <div className="relative bg-[#272727] backdrop-blur-xl rounded-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 bg-[#272727] backdrop-blur-xl border-b border-[#272727]">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Fact Rating</h3>
            <button 
              onClick={() => setIsVoteModalOpen(false)}
              className="text-gray-400 hover:text-white"
            >
              Done
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 bg-[#272727] backdrop-blur-md">
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Current Rating */}
          <div className="flex justify-center space-x-2 opacity-50">
            {getRatingBars('current')}
          </div>

          {/* Voting Interface */}
          <div>
            <h4 className="text-lg font-medium text-white mb-4">Vote Change</h4>
            <div className="flex justify-center space-x-2">
              {getRatingBars('voting')}
            </div>
          </div>

          <div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain your reasoning..."
              className="w-full h-32 px-4 py-3 bg-[#272727] backdrop-blur-md text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600 border border-[#404040] resize-none hover:border-[#505050] transition-colors"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSubmitVote}
              disabled={isSubmitting || !reason.trim()}
              className="px-6 py-2 bg-[#272727] backdrop-blur-md text-white rounded-md hover:bg-[#404040] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Vote'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <button
        onClick={() => setIsVoteModalOpen(true)}
        className="px-3 py-2 rounded-lg bg-[#272727] backdrop-blur-md hover:bg-[#404040] transition-colors flex flex-col items-center space-y-2"
      >
        <div className="flex space-x-1">
          {getRatingBars('current')}
        </div>
        <span className="text-xs text-gray-300">Fact Rating</span>
      </button>

      {modal}
    </>
  );
};

export default FactRating;